import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const TIPOS = {
  carniceria: { label:'🥩 Carnicería', color:'#C0392B', light:'#FADBD8' },
  almacen:    { label:'🛒 Almacén',    color:'#2471A3', light:'#D6EAF8' },
  verduleria: { label:'🥦 Verdulería', color:'#27AE60', light:'#D5F5E3' },
  otro:       { label:'📦 Otro',       color:'#7D3C98', light:'#F4ECF7' },
};

const fmt = n => `$${(n||0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
const hoy = new Date().toISOString().split('T')[0];

export default function Proveedores() {
  const navigate = useNavigate();
  const [proveedores, setProveedores] = useState([]);
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('resumen'); // resumen | nueva
  const [form, setForm] = useState({ proveedor_id:'', fecha:hoy, descripcion:'', cantidad:1, precio_unitario:'', pagado:0, observaciones:'' });
  const [guardando, setGuardando] = useState(false);
  const [exito, setExito] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('todos');

  useEffect(() => { cargarDatos(); }, []);

  async function cargarDatos() {
    setLoading(true);
    const [{ data: provs }, { data: comps }] = await Promise.all([
      supabase.from('proveedores').select('*').order('nombre'),
      supabase.from('compras_proveedores').select('*, proveedores(nombre, tipo)').order('fecha', { ascending: false }),
    ]);
    setProveedores(provs || []);
    setCompras(comps || []);
    setLoading(false);
  }

  function totalDeuda(proveedorId) {
    return compras
      .filter(c => c.proveedor_id === proveedorId)
      .reduce((sum, c) => sum + ((c.total||0) - (c.pagado||0)), 0);
  }

  function totalCompras(proveedorId) {
    return compras
      .filter(c => c.proveedor_id === proveedorId)
      .reduce((sum, c) => sum + (c.total||0), 0);
  }

  function deudaTotal() {
    return compras.reduce((sum, c) => sum + ((c.total||0) - (c.pagado||0)), 0);
  }

  async function guardarCompra() {
    if (!form.proveedor_id || !form.descripcion || !form.precio_unitario) {
      alert('Completá proveedor, descripción y precio');
      return;
    }
    setGuardando(true);
    const { error } = await supabase.from('compras_proveedores').insert({
      proveedor_id: form.proveedor_id,
      fecha: form.fecha,
      descripcion: form.descripcion,
      cantidad: parseFloat(form.cantidad) || 1,
      precio_unitario: parseFloat(form.precio_unitario),
      pagado: parseFloat(form.pagado) || 0,
      observaciones: form.observaciones,
    });
    setGuardando(false);
    if (error) { alert('Error: ' + error.message); return; }
    setExito(true);
    setTimeout(() => setExito(false), 3000);
    setForm({ proveedor_id:'', fecha:hoy, descripcion:'', cantidad:1, precio_unitario:'', pagado:0, observaciones:'' });
    cargarDatos();
    setTab('resumen');
  }

  async function registrarPago(compraId, pagadoActual, total) {
    const monto = prompt(`¿Cuánto pagás? (Deuda: ${fmt(total - pagadoActual)})`);
    if (!monto) return;
    const nuevoPagado = Math.min(pagadoActual + parseFloat(monto), total);
    const { error } = await supabase.from('compras_proveedores').update({ pagado: nuevoPagado }).eq('id', compraId);
    if (error) { alert('Error: ' + error.message); return; }
    cargarDatos();
  }

  const comprasFiltradas = filtroTipo === 'todos'
    ? compras
    : compras.filter(c => c.proveedores?.tipo === filtroTipo);

  const lbl = { fontSize:12, color:'#888', fontWeight:'bold', display:'block', marginBottom:4 };
  const inp = { padding:'8px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:14, outline:'none', width:'100%' };

  return (
    <div style={{ minHeight:'100vh', background:'#F2F3F4' }}>

      {/* Header */}
      <div style={{ background:'#1B2631', padding:'16px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h1 style={{ color:'#fff', margin:0, fontSize:20 }}>🏪 Proveedores</h1>
          <p style={{ color:'#aaa', margin:'2px 0 0', fontSize:12 }}>Compras y deudas por proveedor</p>
        </div>
        <button onClick={() => navigate('/dashboard')}
          style={{ background:'transparent', border:'1px solid #555', color:'#aaa', padding:'8px 16px', borderRadius:8, cursor:'pointer' }}>
          ← Volver
        </button>
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:24 }}>

        {exito && (
          <div style={{ background:'#D5F5E3', border:'1px solid #27AE60', borderRadius:8, padding:12, marginBottom:16, color:'#1E8449', fontWeight:'bold', textAlign:'center' }}>
            ✅ ¡Compra registrada correctamente!
          </div>
        )}

        {/* KPI deuda total */}
        <div style={{ background:'#1B2631', borderRadius:12, padding:20, marginBottom:20, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ color:'#aaa', fontSize:14 }}>💳 Deuda total con proveedores</span>
          <span style={{ color:'#E67E22', fontWeight:'bold', fontSize:24 }}>{fmt(deudaTotal())}</span>
        </div>

        {/* Cards por proveedor */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(250px, 1fr))', gap:12, marginBottom:20 }}>
          {proveedores.map(p => {
            const tipo = TIPOS[p.tipo] || TIPOS.otro;
            const deuda = totalDeuda(p.id);
            const total = totalCompras(p.id);
            return (
              <div key={p.id} style={{ background:'#fff', borderRadius:12, padding:16, borderLeft:`4px solid ${tipo.color}`, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize:14, fontWeight:'bold', color:'#1B2631', marginBottom:8 }}>{tipo.label} — {p.nombre}</div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                  <span style={{ color:'#888' }}>Total compras:</span>
                  <span style={{ fontWeight:'bold', color:'#555' }}>{fmt(total)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                  <span style={{ color:'#888' }}>Deuda pendiente:</span>
                  <span style={{ fontWeight:'bold', color: deuda > 0 ? '#C0392B' : '#27AE60' }}>
                    {deuda > 0 ? fmt(deuda) : '✅ Al día'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          {[['resumen','📋 Ver Compras'],['nueva','➕ Nueva Compra']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ padding:'10px 20px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:'bold', fontSize:14,
                background: tab===key ? '#E67E22' : '#fff',
                color: tab===key ? '#fff' : '#555',
                boxShadow:'0 2px 6px rgba(0,0,0,0.08)'
              }}>
              {label}
            </button>
          ))}
        </div>

        {/* Nueva compra */}
        {tab === 'nueva' && (
          <div style={{ background:'#fff', borderRadius:12, padding:24, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
            <h2 style={{ margin:'0 0 20px', fontSize:16, color:'#1B2631' }}>➕ Registrar Compra</h2>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <div>
                <label style={lbl}>Proveedor *</label>
                <select value={form.proveedor_id} onChange={e => setForm({...form, proveedor_id:e.target.value})} style={inp}>
                  <option value="">Seleccioná...</option>
                  {proveedores.map(p => (
                    <option key={p.id} value={p.id}>{TIPOS[p.tipo]?.label} — {p.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl}>Fecha *</label>
                <input type="date" value={form.fecha} onChange={e => setForm({...form, fecha:e.target.value})} style={inp} />
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Descripción *</label>
                <input placeholder="Ej: 10kg asado, 5kg pollo..." value={form.descripcion} onChange={e => setForm({...form, descripcion:e.target.value})} style={inp} />
              </div>
              <div>
                <label style={lbl}>Cantidad</label>
                <input type="number" value={form.cantidad} onChange={e => setForm({...form, cantidad:e.target.value})} style={inp} />
              </div>
              <div>
                <label style={lbl}>Precio Unitario (ARS) *</label>
                <input type="number" placeholder="0" value={form.precio_unitario} onChange={e => setForm({...form, precio_unitario:e.target.value})} style={inp} />
              </div>
              <div>
                <label style={lbl}>Pagado en el momento (ARS)</label>
                <input type="number" placeholder="0" value={form.pagado} onChange={e => setForm({...form, pagado:e.target.value})} style={inp} />
              </div>
              <div>
                <label style={lbl}>Total estimado</label>
                <div style={{ padding:'8px 10px', background:'#F8F9FA', borderRadius:8, fontWeight:'bold', color:'#27AE60', fontSize:16 }}>
                  {fmt((parseFloat(form.cantidad)||1) * (parseFloat(form.precio_unitario)||0))}
                </div>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Observaciones</label>
                <input placeholder="Opcional..." value={form.observaciones} onChange={e => setForm({...form, observaciones:e.target.value})} style={inp} />
              </div>
            </div>
            <button onClick={guardarCompra} disabled={guardando}
              style={{ marginTop:20, width:'100%', padding:14, background: guardando ? '#aaa' : '#27AE60', color:'#fff', border:'none', borderRadius:10, fontSize:16, fontWeight:'bold', cursor: guardando ? 'not-allowed' : 'pointer' }}>
              {guardando ? 'Guardando...' : '💾 Guardar Compra'}
            </button>
          </div>
        )}

        {/* Lista de compras */}
        {tab === 'resumen' && (
          <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:8 }}>
              <h2 style={{ margin:0, fontSize:16, color:'#1B2631' }}>📋 Historial de Compras</h2>
              <div style={{ display:'flex', gap:8 }}>
                {[['todos','Todos'],['carniceria','🥩'],['almacen','🛒'],['verduleria','🥦']].map(([key, label]) => (
                  <button key={key} onClick={() => setFiltroTipo(key)}
                    style={{ padding:'6px 14px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:'bold',
                      background: filtroTipo===key ? '#1B2631' : '#eee',
                      color: filtroTipo===key ? '#fff' : '#555'
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign:'center', padding:30, color:'#888' }}>Cargando...</div>
            ) : comprasFiltradas.length === 0 ? (
              <div style={{ textAlign:'center', padding:30, color:'#888' }}>No hay compras registradas</div>
            ) : (
              comprasFiltradas.map((c, i) => {
                const tipo = TIPOS[c.proveedores?.tipo] || TIPOS.otro;
                const saldo = (c.total||0) - (c.pagado||0);
                return (
                  <div key={c.id} style={{ borderBottom:'1px solid #f5f5f5', padding:'12px 0', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <span style={{ background:tipo.light, color:tipo.color, padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:'bold' }}>
                          {tipo.label}
                        </span>
                        <span style={{ fontSize:12, color:'#aaa' }}>
                          {new Date(c.fecha+'T12:00:00').toLocaleDateString('es-AR')}
                        </span>
                      </div>
                      <div style={{ fontSize:14, fontWeight:'bold', color:'#1B2631' }}>{c.descripcion}</div>
                      <div style={{ fontSize:12, color:'#888' }}>
                        {c.cantidad} u. × {fmt(c.precio_unitario)} = {fmt(c.total)}
                        {c.observaciones && <span style={{ marginLeft:8, fontStyle:'italic' }}>{c.observaciones}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:13, color:'#888' }}>Pagado: {fmt(c.pagado)}</div>
                      <div style={{ fontSize:15, fontWeight:'bold', color: saldo > 0 ? '#C0392B' : '#27AE60' }}>
                        {saldo > 0 ? `Debe: ${fmt(saldo)}` : '✅ Pagado'}
                      </div>
                      {saldo > 0 && (
                        <button onClick={() => registrarPago(c.id, c.pagado, c.total)}
                          style={{ marginTop:4, padding:'4px 12px', background:'#E67E22', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:'bold' }}>
                          💰 Registrar Pago
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

      </div>
    </div>
  );
}